/**
 * Language Utilities
 * Unicode-aware text processing for multilingual chat
 */

import { getRuntimeRtlChecker } from '@/lib/runtimeRtlChecker';

/**
 * locale-data: bootstrap-rtl-fallback - Minimal bootstrap set for SSR/initial load before DB is available
 * These are only used as fallback when database configs haven't loaded yet.
 * Source of truth: system_locale_configs.is_rtl column
 */
const BOOTSTRAP_RTL_LANGUAGES = new Set(['ar', 'he', 'fa', 'ur', 'yi', 'ps', 'sd']);

/**
 * Check if a language code is RTL
 * Uses database is_rtl column when available, falls back to bootstrap set
 */
export function isRTL(languageCode: string | null | undefined): boolean {
  if (!languageCode) return false;
  
  // Try database-driven RTL check first (set by useI18nInitializer after DB loads)
  const runtimeChecker = getRuntimeRtlChecker();
  if (runtimeChecker) {
    return runtimeChecker(languageCode);
  }
  
  // Bootstrap fallback: Extract base language code (e.g., 'ar' from 'ar-SA')
  const baseCode = languageCode.split('-')[0].toLowerCase();
  return BOOTSTRAP_RTL_LANGUAGES.has(baseCode);
}

/**
 * Get text direction for a language
 */
export function getTextDirection(languageCode: string | null | undefined): 'ltr' | 'rtl' {
  return isRTL(languageCode) ? 'rtl' : 'ltr';
}

/**
 * Truncate text by grapheme clusters (safe for emoji and combined characters)
 * Uses Intl.Segmenter when available, falls back to Array.from
 */
export function truncateByGraphemes(text: string, maxLength: number, ellipsis = '…'): string {
  if (!text) return '';
  
  // Use Intl.Segmenter if available (modern browsers)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const IntlAny = Intl as any;
  if (typeof IntlAny !== 'undefined' && 'Segmenter' in IntlAny) {
    try {
      const segmenter = new IntlAny.Segmenter(undefined, { granularity: 'grapheme' });
      const segments = Array.from(segmenter.segment(text)) as { segment: string }[];
      
      if (segments.length <= maxLength) return text;
      
      const truncated = segments.slice(0, maxLength).map(s => s.segment).join('');
      return truncated + ellipsis;
    } catch {
      // Fall through to fallback
    }
  }
  
  // Fallback: Array.from handles most Unicode correctly
  const chars = Array.from(text);
  if (chars.length <= maxLength) return text;
  
  return chars.slice(0, maxLength).join('') + ellipsis;
}

/**
 * Generate smart title from user message (Unicode-aware)
 * Preserves all Unicode characters, no stop words needed
 */
export function generateSmartTitle(userMessage: string, maxLength = 40): string {
  if (!userMessage || userMessage.trim().length === 0) {
    return 'New Chat';
  }
  
  // Clean and normalize the message
  const cleaned = userMessage
    .trim()
    // Normalize whitespace
    .replace(/\s+/g, ' ')
    // Remove only control characters and some punctuation at the end
    .replace(/[\u0000-\u001F\u007F-\u009F]|[.!?;:]+$/g, '');
  
  // Truncate by graphemes to respect combined characters
  return truncateByGraphemes(cleaned, maxLength);
}

/**
 * Generate a concise 2-4 word title from user message
 * Creates intuitive, short titles for chat sessions
 * 
 * Examples:
 * - "How do I file my US tax return?" → "File US taxes"
 * - "What are the tax implications of moving to Portugal?" → "Moving to Portugal"
 * - "Best strategies for wealth protection" → "Wealth protection strategies"
 */
export function generateConciseTitle(userMessage: string): string {
  if (!userMessage || userMessage.trim().length === 0) {
    return 'New Chat';
  }
  
  // Clean the message
  let cleaned = userMessage
    .trim()
    .replace(/\s+/g, ' ')
    // Remove common question prefixes
    .replace(/^(how (do|can|should|would) (i|we|you)|what (is|are|about)|can (i|you|we)|i (want|need|would like) to|please|help me( to)?|i'm (looking|trying) to)\s+/gi, '')
    // Remove trailing punctuation
    .replace(/[.!?;:,]+$/g, '')
    // Remove quotes
    .replace(/["""'']/g, '');
  
  // Split into words
  const words = cleaned.split(/\s+/).filter(w => w.length > 0);
  
  if (words.length === 0) {
    return 'New Chat';
  }
  
  // Common stop words to skip (but keep substantive words)
  const stopWords = new Set([
    'the', 'a', 'an', 'of', 'to', 'for', 'in', 'on', 'at', 'by', 'with',
    'and', 'or', 'my', 'your', 'our', 'their', 'his', 'her', 'its',
    'is', 'are', 'was', 'were', 'be', 'been', 'being',
    'have', 'has', 'had', 'do', 'does', 'did',
    'will', 'would', 'could', 'should', 'may', 'might', 'can',
    'if', 'when', 'where', 'why', 'how', 'what', 'which', 'who',
    'this', 'that', 'these', 'those', 'it', 'they', 'we', 'i', 'you',
    'about', 'some', 'any', 'all', 'most', 'more', 'less', 'much', 'many',
    'best', 'good', 'new', 'first', 'last', 'own', 'same', 'different'
  ]);
  
  // Extract key words (non-stop words, capitalized proper nouns kept)
  const keyWords: string[] = [];
  for (const word of words) {
    const lowerWord = word.toLowerCase();
    // Keep numbers and words not in stop list, or capitalized words (likely proper nouns)
    if (!stopWords.has(lowerWord) || (word[0] === word[0].toUpperCase() && word.length > 2)) {
      keyWords.push(word);
    }
    // Stop at 4 key words
    if (keyWords.length >= 4) break;
  }
  
  // If we got less than 2 key words, take first few words instead
  if (keyWords.length < 2) {
    const fallbackWords = words.slice(0, 3);
    return fallbackWords.map(w => capitalizeFirst(w)).join(' ');
  }
  
  // Capitalize first letter of each word for title case
  const title = keyWords.map(w => capitalizeFirst(w)).join(' ');
  
  // Truncate if still too long (max ~30 chars for UI)
  return truncateByGraphemes(title, 30);
}

/**
 * Capitalize first letter of a word
 */
function capitalizeFirst(word: string): string {
  if (!word) return word;
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

/**
 * Quick heuristic language detection based on script (no API call)
 * Returns ISO 639-1 code or null if uncertain
 */
export function detectLanguageByScript(text: string): { code: string; confidence: number } | null {
  if (!text || text.trim().length < 3) return null;
  
  // CJK characters (Chinese, Japanese partial, Korean partial)
  if (/[\u4e00-\u9fff]/.test(text)) {
    // Could be Chinese, Japanese with Kanji, or Korean Hanja
    // Check for Japanese-specific kana
    if (/[\u3040-\u309f\u30a0-\u30ff]/.test(text)) {
      return { code: 'ja', confidence: 0.95 };
    }
    // Check for Korean Hangul
    if (/[\uac00-\ud7af]/.test(text)) {
      return { code: 'ko', confidence: 0.95 };
    }
    return { code: 'zh', confidence: 0.90 };
  }
  
  // Japanese Hiragana/Katakana only
  if (/[\u3040-\u309f\u30a0-\u30ff]/.test(text)) {
    return { code: 'ja', confidence: 0.95 };
  }
  
  // Korean Hangul
  if (/[\uac00-\ud7af]/.test(text)) {
    return { code: 'ko', confidence: 0.95 };
  }
  
  // Arabic script
  if (/[\u0600-\u06ff\u0750-\u077f]/.test(text)) {
    return { code: 'ar', confidence: 0.95 };
  }
  
  // Hebrew script
  if (/[\u0590-\u05ff]/.test(text)) {
    return { code: 'he', confidence: 0.95 };
  }
  
  // Russian/Cyrillic
  if (/[\u0400-\u04ff]/.test(text)) {
    return { code: 'ru', confidence: 0.90 };
  }
  
  // Hindi/Devanagari
  if (/[\u0900-\u097f]/.test(text)) {
    return { code: 'hi', confidence: 0.95 };
  }
  
  // Thai
  if (/[\u0e00-\u0e7f]/.test(text)) {
    return { code: 'th', confidence: 0.95 };
  }
  
  // Greek
  if (/[\u0370-\u03ff]/.test(text)) {
    return { code: 'el', confidence: 0.95 };
  }
  
  // Latin-based languages - detect common patterns
  const textLower = text.toLowerCase();
  
  // Spanish patterns
  if (/\b(qué|cómo|por qué|cuándo|dónde|impuesto|fiscal|tributario)\b/i.test(text) ||
      /[áéíóúñ¿¡]/.test(text)) {
    return { code: 'es', confidence: 0.80 };
  }
  
  // Portuguese patterns
  if (/\b(qual|como|porque|quando|onde|imposto|tributário)\b/i.test(text) ||
      /[ãõç]/.test(text) && !/ñ/.test(text)) {
    return { code: 'pt', confidence: 0.80 };
  }
  
  // French patterns
  if (/\b(comment|pourquoi|quand|où|impôt|fiscal)\b/i.test(text) ||
      /[àâäéèêëïîôùûüÿœæç]/.test(text) && /\b(le|la|les|un|une|des)\b/i.test(text)) {
    return { code: 'fr', confidence: 0.80 };
  }
  
  // German patterns
  if (/\b(wie|warum|wann|wo|steuer|finanzamt)\b/i.test(text) ||
      /[äöüß]/.test(text)) {
    return { code: 'de', confidence: 0.80 };
  }
  
  // Italian patterns
  if (/\b(come|perché|quando|dove|tassa|imposta|fiscale)\b/i.test(text)) {
    return { code: 'it', confidence: 0.75 };
  }
  
  // Default to English if mostly ASCII
  if (/^[\x00-\x7F\s]+$/.test(text)) {
    return { code: 'en', confidence: 0.70 };
  }
  
  // Uncertain - has diacritics but couldn't determine language
  return null;
}

/**
 * Combined language detection: fast heuristic first, then AI fallback if needed
 */
export async function detectLanguageWithFallback(
  text: string,
  aiDetector?: (text: string) => Promise<{ detected_language: string; confidence: number } | null>
): Promise<{ code: string; confidence: number }> {
  // First try script-based detection (instant, no API call)
  const scriptResult = detectLanguageByScript(text);
  
  // If high confidence from script detection, use it
  if (scriptResult && scriptResult.confidence >= 0.80) {
    return scriptResult;
  }
  
  // If AI detector provided and we're uncertain, use it
  if (aiDetector) {
    try {
      const aiResult = await aiDetector(text);
      if (aiResult && aiResult.confidence > (scriptResult?.confidence || 0)) {
        return { code: aiResult.detected_language, confidence: aiResult.confidence };
      }
    } catch {
      // Silently fall through to default
    }
  }
  
  // Return script result if available, otherwise default to English
  return scriptResult || { code: 'en', confidence: 0.50 };
}
